import { MapPin } from "lucide-react";
import type { EventPlatform } from "@shared/eventPlatform";

/**
 * The service mark for a meeting platform.
 *
 * Drawn inline rather than fetched: these sit inside a dropdown that opens
 * instantly, and four small marks are not worth four network requests or an icon
 * dependency. Each is a simplified glyph in the service's own colour, which is
 * what makes the option recognizable at a glance.
 *
 * "Other" is a place rather than a service, so it takes a map pin.
 */
export default function EventPlatformIcon({
  platform,
  size = 18,
}: {
  platform: EventPlatform;
  size?: number;
}) {
  const shared = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    "aria-hidden": true as const,
    style: { flex: "0 0 auto", display: "block" },
  };

  if (platform === "Zoom") {
    return (
      <svg {...shared}>
        <rect width="24" height="24" rx="6" fill="#2D8CFF" />
        <path d="M5.4 9.1a1 1 0 0 1 1-1h6.3a1.7 1.7 0 0 1 1.7 1.7v4.1a1.7 1.7 0 0 1-1.7 1.7H6.4a1 1 0 0 1-1-1V9.1Z" fill="#fff" />
        <path d="M15.5 11.2l2.6-1.9a.6.6 0 0 1 1 .5v4.4a.6.6 0 0 1-1 .5l-2.6-1.9v-1.6Z" fill="#fff" />
      </svg>
    );
  }

  if (platform === "Google Meet") {
    return (
      <svg {...shared}>
        <path d="M3 8.6A1.6 1.6 0 0 1 4.6 7h7.6v10H4.6A1.6 1.6 0 0 1 3 15.4V8.6Z" fill="#00832D" />
        <path d="M12.2 7h4.3v4.3l-4.3 1.4V7Z" fill="#FFBA00" />
        <path d="M12.2 17h4.3v-4.3l-4.3-1.4V17Z" fill="#0066DA" />
        <path d="M16.5 10.3l3.5-2.2a.6.6 0 0 1 1 .5v6.8a.6.6 0 0 1-1 .5l-3.5-2.2v-3.4Z" fill="#E94235" />
      </svg>
    );
  }

  if (platform === "Microsoft Teams Meeting") {
    return (
      <svg {...shared}>
        <rect x="2" y="5" width="12.5" height="14" rx="2.2" fill="#5059C9" />
        <path d="M5 8.6h6.6v1.7H9.2V16H7.4v-5.7H5V8.6Z" fill="#fff" />
        <circle cx="18.4" cy="8.1" r="2.6" fill="#7B83EB" />
        <path d="M15.2 11.6H21a1 1 0 0 1 1 1v2.7a3.9 3.9 0 0 1-6.8 2.6v-6.3Z" fill="#7B83EB" />
      </svg>
    );
  }

  return <MapPin size={size} aria-hidden="true" style={{ flex: "0 0 auto", display: "block", color: "#9FB0C6" }} />;
}
